/**
 * PopWiz App — API 服务层 (带 Token 认证)
 */
import { captureException, captureMessage } from '../lib/sentry';

export const API_BASE = import.meta.env.VITE_API_URL || '/api';

/** Error thrown by API wrappers when the server responds non-2xx. Carries the
 *  HTTP status so callers (e.g. CanvasErrorState) can render 403/404-specific
 *  copy without parsing error messages. */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function getToken(): string | null {
  // Deprecated compatibility path. New sessions are also carried by the
  // HttpOnly `popwiz_session` cookie; keep Bearer while older tabs migrate.
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('popwiz_token');
  } catch {
    return null;
  }
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function withApiCredentials(init: RequestInit = {}): RequestInit {
  return {
    ...init,
    credentials: init.credentials ?? 'include',
  };
}

/* ── Bug 6.U1 payment grace ─────────────────────────────────────────── */
const GRACE_KEY = 'popwiz_payment_grace';
const GRACE_SWALLOWED_KEY = 'popwiz_payment_grace_swallowed';
const GRACE_WINDOW_MS = 30_000;

export function setPaymentGrace(): void {
  try {
    sessionStorage.setItem(GRACE_KEY, String(Date.now() + GRACE_WINDOW_MS));
    sessionStorage.removeItem(GRACE_SWALLOWED_KEY);
  } catch { /* sessionStorage may be disabled */ }
}

function isGraceActive(): boolean {
  try {
    const until = Number(sessionStorage.getItem(GRACE_KEY) || 0);
    return until > Date.now();
  } catch { return false; }
}

function consumeGraceOnce(): boolean {
  // Only swallow ONE 401 per grace window — if the second 401 comes in we
  // really are logged out and need to go to login.
  try {
    if (!isGraceActive()) return false;
    if (sessionStorage.getItem(GRACE_SWALLOWED_KEY) === '1') return false;
    sessionStorage.setItem(GRACE_SWALLOWED_KEY, '1');
    return true;
  } catch { return false; }
}

export function handleUnauthorized(res: Response): Response {
  if (res.status === 401 && !window.location.pathname.endsWith('/login')) {
    if (consumeGraceOnce()) {
      // Inside the post-checkout grace window — silently let this 401 pass.
      // The next failed call will eject normally.
      return res;
    }
    // Bug 6.U5: only clear auth on involuntary 401, keep user prefs intact.
    clearAuth();
    window.location.href = '/app/login';
  }
  if (res.status === 403 && !window.location.pathname.endsWith('/forbidden')) {
    window.location.href = '/app/forbidden';
  }
  return res;
}

// Cloudflare edge→origin errors: transient, not actionable from frontend.
const CF_ORIGIN_DOWN = new Set([521, 522, 523, 524]);
// Per (status:url) dedupe — one Sentry capture per minute per signature.
const SENTRY_5XX_DEDUPE_MS = 60_000;
const sentry5xxLastSeen = new Map<string, number>();

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, withApiCredentials(init));
  if (res.status >= 500 && res.status < 600 && !CF_ORIGIN_DOWN.has(res.status)) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method || 'GET';
    const key = `${res.status}:${method}:${url}`;
    const now = Date.now();
    const last = sentry5xxLastSeen.get(key) || 0;
    if (now - last >= SENTRY_5XX_DEDUPE_MS) {
      sentry5xxLastSeen.set(key, now);
      captureMessage(`[api ${res.status}] ${method} ${url}`, 'error');
    }
  }
  return handleUnauthorized(res);
}

export async function login(username: string, password: string) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '登录失败' }));
    throw new Error(err.detail);
  }
  const data = await res.json();
  localStorage.setItem('popwiz_token', data.token);
  localStorage.setItem('popwiz_user', JSON.stringify({
    username: data.username,
    credits: data.credits,
    subscription_credits: data.subscription_credits ?? 0,
    total_credits: data.total_credits ?? data.credits,
  }));
  // Login/register are SPA navigations — if a prior session ended without an
  // explicit logout (e.g. a 5xx getMe that SPA-redirects to /app/login), its
  // project caches survive. Purge — and AWAIT it — so the reset completes
  // before this resolves and the caller navigates to /app; a fire-and-forget
  // import would let MainPage render the prior account's cached list before
  // the reset runs (PR #566 GitHub review, codex P1). studioCache is already
  // loaded (static import elsewhere), so the dynamic import is a resolved
  // microtask — no circular-eval concern.
  await import('../lib/studioCache').then(m => m.resetStudioCaches()).catch(() => {});
  return data;
}

export async function register(
  username: string,
  email: string,
  password: string,
  lang: string = 'en',
  turnstileToken: string = '',
  referralCode?: string
) {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      username, email, password, lang, turnstile_token: turnstileToken,
      referral_code: referralCode || undefined,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '注册失败' }));
    const msg = Array.isArray(err.detail)
      ? err.detail.map((d: any) => d?.msg).filter(Boolean).join('; ')
      : (typeof err.detail === 'string' ? err.detail : '');
    throw new Error(msg || '注册失败');
  }
  const data = await res.json();
  localStorage.setItem('popwiz_token', data.token);
  localStorage.setItem('popwiz_user', JSON.stringify({
    username: data.username,
    credits: data.credits,
    subscription_credits: data.subscription_credits ?? 0,
    total_credits: data.total_credits ?? data.credits,
  }));
  // Login/register are SPA navigations — if a prior session ended without an
  // explicit logout (e.g. a 5xx getMe that SPA-redirects to /app/login), its
  // project caches survive. Purge — and AWAIT it — so the reset completes
  // before this resolves and the caller navigates to /app; a fire-and-forget
  // import would let MainPage render the prior account's cached list before
  // the reset runs (PR #566 GitHub review, codex P1). studioCache is already
  // loaded (static import elsewhere), so the dynamic import is a resolved
  // microtask — no circular-eval concern.
  await import('../lib/studioCache').then(m => m.resetStudioCaches()).catch(() => {});
  return data;
}

export async function getMe() {
  const res = await apiFetch(`${API_BASE}/me`, { headers: authHeaders() });
  if (!res.ok) throw new Error('未登录');
  return res.json();
}

/** Bug 6.U5: clear auth-only state. Used by involuntary 401 redirects so we
 *  don't nuke the user's language / model / canvas preferences just because
 *  their token expired. Explicit user logout still calls full `logout()`. */
export function clearAuth() {
  localStorage.removeItem('popwiz_token');
  localStorage.removeItem('popwiz_user');
}

export async function logout() {
  // 通知后端注销 token/cookie（best-effort，不阻塞）
  fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
  }).catch(() => {});
  clearAuth();
  // SPA navigation keeps module state alive — purge the project list/prefetch
  // caches so the next account never sees this one's titles/covers (r6 opus
  // P3). AWAIT so callers (Header/VerifyEmail `await logout()`) finish the
  // reset before navigating (PR #566 GitHub review, codex P1). Dynamic import
  // avoids the static studioCache→apiService cycle; the module is already
  // loaded so it resolves immediately.
  await import('../lib/studioCache').then(m => m.resetStudioCaches()).catch(() => {});
  // 用户偏好
  localStorage.removeItem('popwiz_studio_mode');
  localStorage.removeItem('popwiz_video_model');
  localStorage.removeItem('popwiz_video_resolution');
  localStorage.removeItem('popwiz_video_duration');
  localStorage.removeItem('popwiz_video_aspect');
  localStorage.removeItem('popwiz_default_video_aspect');
  localStorage.removeItem('popwiz_default_video_duration');
  localStorage.removeItem('popwiz_image_model');
  localStorage.removeItem('popwiz_image_size');
  localStorage.removeItem('popwiz_image_ratio');
  localStorage.removeItem('popwiz_chat_width');
  localStorage.removeItem('popwiz_lang');
  // 会话状态
  sessionStorage.removeItem('popwiz_studio_canvas');
  sessionStorage.removeItem('popwiz_studio_chat');
  sessionStorage.removeItem('popwiz_pending_images');
  sessionStorage.removeItem('popwiz_active_video_jobs');
  sessionStorage.removeItem('popwiz_studio_session_id');
}

export function isLoggedIn(): boolean {
  const token = getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      logout();
      return false;
    }
  } catch {
    return false;
  }
  return true;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ── Email verification ── */

export async function sendVerifyCode(lang: string = 'en') {
  const res = await apiFetch(`${API_BASE}/auth/verify/send`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ lang }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '发送失败' }));
    throw new Error(err.detail);
  }
  return res.json();
}

export async function confirmVerifyCode(code: string) {
  const res = await apiFetch(`${API_BASE}/auth/verify/confirm`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '验证失败' }));
    throw new Error(err.detail);
  }
  return res.json();
}

/* ── Password reset & change ── */

export async function requestPasswordReset(email: string, lang: string = 'en'): Promise<void> {
  // Enumeration-resistant: always resolves, never throws on 200/400.
  await fetch(`${API_BASE}/auth/password/reset/request`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, lang }),
  }).catch(() => {});
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/password/reset/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '重置失败' }));
    throw new Error(err.detail);
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}/auth/password/change`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '修改失败' }));
    throw new Error(err.detail);
  }
}

/* ── Billing ── */

export async function getPlans(lang: string = 'en') {
  const res = await fetch(`${API_BASE}/billing/plans?lang=${encodeURIComponent(lang)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('获取方案失败');
  return res.json();
}

export async function createCheckout(planId: string, lang: string = 'en') {
  const res = await apiFetch(`${API_BASE}/billing/checkout`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan_id: planId, lang }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '创建订单失败' }));
    throw new Error(err.detail);
  }
  return res.json();
}

export async function getSubscription() {
  const res = await apiFetch(`${API_BASE}/billing/subscription`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('获取订阅状态失败');
  return res.json();
}

export async function createPortalSession() {
  const res = await apiFetch(`${API_BASE}/billing/portal`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('创建管理页面失败');
  return res.json();
}

/* Bug 6.U2/U4: poll Stripe-side state from MyPlanPage after Checkout redirect. */
export type VerifyCheckoutResult =
  | { status: 'paid'; credits_added: number; current_credits: number; pack_expires_at: string | null }
  | { status: 'pending' }
  | { status: 'failed'; error?: string };

export async function verifyCheckoutSession(sessionId: string): Promise<VerifyCheckoutResult> {
  const res = await apiFetch(
    `${API_BASE}/billing/verify-session?session_id=${encodeURIComponent(sessionId)}`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    if (res.status === 404) return { status: 'failed', error: 'unknown_session' };
    throw new Error('verify_session_failed');
  }
  return res.json();
}

/** 获取带 auth 的 transport headers */
export function getAuthHeaders(): Record<string, string> {
  return authHeaders();
}

/* ── Studio Project Persistence ── */

export interface StudioProjectSummary {
  id: string;
  title: string;
  thumbnail_url: string | null;
  thumbnail_color: string | null;
  updated_at: string | null;
}

export interface StudioProjectDetail {
  id: string;
  title: string;
  thumbnail_url: string | null;
  thumbnail_color: string | null;
  canvas_version: number;
  canvas_state: { results: any[]; nextPos: { col: number; row: number } } | null;
  backend_session_id: string | null;
  updated_at: string | null;
  messages: {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    source?: string;
    files: { url: string; r2Key?: string; mediaType: string; posterUrl?: string }[];
    toolCalls: { name: string; label: string; cost: number; status: string; jobId?: string; clientRequestId?: string }[];
    userImages: string[];
  }[];
}

export interface StudioProjectListResponse {
  items: StudioProjectSummary[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface StudioEntitlement {
  max_projects: number;
  ttl_seconds: number | null;
  is_subscribed: boolean;
  auto_evict_oldest_project: boolean;
}

export interface StudioProjectPreferences {
  auto_evict_oldest_project: boolean;
  auto_evict_oldest_project_enabled_at: string | null;
}

// Studio entitlement defaults: serve as BOTH the pre-resolve initial state
// (MainPage useState) AND the post-failure fallback in getStudioEntitlement
// below. Two roles, but they MUST share one value — diverging them is exactly
// how PR #284's `useState(1)` bug appeared.
//
// Source of truth: backend/studio_api.py:FREE_MAX_PROJECTS. The two literals
// live in different languages; runtime drift is detected by the e2e scenario
// in frontend/e2e/studio-quota-cap.spec.ts (not a build-time guarantee).
export const STUDIO_DEFAULT_MAX_PROJECTS = 100;
export const STUDIO_DEFAULT_TTL_SECONDS: number | null = null;

export async function listStudioProjects(
  opts: { limit?: number; offset?: number } = {},
): Promise<StudioProjectListResponse> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const query = params.toString();
  const res = await apiFetch(`${API_BASE}/v1/studio/projects${query ? `?${query}` : ''}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to list projects');
  return res.json();
}

export class CapReachedError extends ApiError {
  readonly current: number;
  readonly max: number;
  constructor(current: number, max: number) {
    super('project_cap_reached', 409);
    this.name = 'CapReachedError';
    this.current = current;
    this.max = max;
  }
}

/**
 * Server state is over cap (data drift, e.g. cap lowered, or a bug created an
 * over-cap state). Backend fails closed — it does NOT delete-one-then-create
 * (that would leave 12/10 → 11/10, still over cap). PR #292 round-2 (Codex v4).
 *
 * Extends CapReachedError on purpose: any existing `instanceof CapReachedError`
 * catch (CanvasPage Stage 1) routes this through the redirect-to-?cap=1 path
 * for free — no new branch needed. Code that wants to distinguish data-drift
 * from normal cap-reached can do `instanceof ProjectCapExceededError`.
 */
export class ProjectCapExceededError extends CapReachedError {
  constructor(current: number, max: number) {
    super(current, max);
    this.name = 'ProjectCapExceededError';
  }
}

export async function createStudioProject(
  opts: { idempotencyKey?: string } = {},
): Promise<{ id: string }> {
  const reqBody: Record<string, string> = {};
  if (opts.idempotencyKey) reqBody.idempotency_key = opts.idempotencyKey;
  const init: RequestInit =
    Object.keys(reqBody).length > 0
      ? {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(reqBody),
        }
      : { method: 'POST', headers: authHeaders() };
  const res = await apiFetch(`${API_BASE}/v1/studio/projects`, init);
  if (res.status === 409) {
    const body = await res.json().catch(() => null) as
      { code?: string; current?: number; max?: number } | null;
    if (body?.code === 'project_cap_exceeded') {
      throw new ProjectCapExceededError(body.current ?? 0, body.max ?? STUDIO_DEFAULT_MAX_PROJECTS);
    }
    if (body?.code === 'project_cap_reached') {
      throw new CapReachedError(body.current ?? 0, body.max ?? STUDIO_DEFAULT_MAX_PROJECTS);
    }
    throw new ApiError('Failed to create project', 409);
  }
  if (!res.ok) throw new ApiError('Failed to create project', res.status);
  return res.json();
}

export async function updateStudioProjectPreferences(
  autoEvictOldestProject: boolean,
): Promise<StudioProjectPreferences> {
  const res = await apiFetch(`${API_BASE}/v1/studio/project-preferences`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ auto_evict_oldest_project: autoEvictOldestProject }),
  });
  if (!res.ok) throw new ApiError('Failed to update project preferences', res.status);
  return res.json();
}

export async function getStudioProject(pid: string): Promise<StudioProjectDetail> {
  const res = await apiFetch(`${API_BASE}/v1/studio/projects/${pid}`, { headers: authHeaders() });
  if (!res.ok) throw new ApiError('Failed to get project', res.status);
  return res.json();
}

export async function deleteStudioProject(pid: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}/v1/studio/projects/${pid}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new ApiError('Failed to delete project', res.status);
}

export async function saveStudioMessages(pid: string, messages: Record<string, unknown>[]): Promise<void> {
  const res = await apiFetch(`${API_BASE}/v1/studio/projects/${pid}/messages`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(messages),
  });
  // apiFetch resolves on any status — without this check a 500 looked like a
  // confirmed save: callers cleared their retry/unload-flush state and the
  // message could vanish on close (adversarial r5, codex P2; siblings
  // saveStudioCanvas/deleteStudioProject already throw).
  if (!res.ok) throw new ApiError('Failed to save messages', res.status);
}

export interface SaveCanvasRequest extends Record<string, unknown> {
  base_canvas_version: number;
  ack_job_ids: string[];
}

export interface SaveCanvasResponse {
  canvas_version: number;
}

export async function saveStudioCanvas(pid: string, state: SaveCanvasRequest): Promise<SaveCanvasResponse> {
  const res = await apiFetch(`${API_BASE}/v1/studio/projects/${pid}/canvas`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(state),
  });
  if (!res.ok) throw new ApiError('Failed to save canvas', res.status);
  return res.json();
}

// One Sentry capture per minute for the entitlement fallback path, so a
// backend outage doesn't flood Sentry with one event per call (the homepage
// may invoke this on every visit). Stored in localStorage so dedupe is
// origin-wide (across tabs), not just per-tab.
const ENTITLEMENT_SENTRY_DEDUPE_MS = 60_000;
const ENTITLEMENT_SENTRY_LAST_KEY = 'popwiz_entitlement_sentry_last';

export async function getStudioEntitlement(): Promise<StudioEntitlement> {
  const fallback: StudioEntitlement = {
    max_projects: STUDIO_DEFAULT_MAX_PROJECTS,
    ttl_seconds: STUDIO_DEFAULT_TTL_SECONDS,
    is_subscribed: false,
    auto_evict_oldest_project: false,
  };
  try {
    const res = await apiFetch(`${API_BASE}/v1/studio/entitlement`, { headers: authHeaders() });
    if (!res.ok) {
      // Non-2xx is already captured upstream by apiFetch's 5xx Sentry hook.
      // Return fallback to preserve UX, not to hide errors.
      return fallback;
    }
    return await res.json();
  } catch (err) {
    // Network throw / abort / JSON parse failure. Capture so a backend outage
    // surfaces to monitoring instead of being silently absorbed into UX.
    const now = Date.now();
    try {
      const last = Number(localStorage.getItem(ENTITLEMENT_SENTRY_LAST_KEY) || 0);
      if (now - last >= ENTITLEMENT_SENTRY_DEDUPE_MS) {
        localStorage.setItem(ENTITLEMENT_SENTRY_LAST_KEY, String(now));
        captureException(err, { extra: { source: 'getStudioEntitlement' } });
      }
    } catch {
      // localStorage unavailable (e.g. private-browsing quota): fall back to
      // a single capture per call (no dedupe) rather than silencing the error.
      captureException(err, { extra: { source: 'getStudioEntitlement' } });
    }
    return fallback;
  }
}

/** Pillar D2: real cancellation — flips status to canceled and refunds credits.
 *  D3 will wire this into the stop button. Safe to no-op on already-terminal jobs. */
export async function cancelJob(jobId: string): Promise<{ job_id: string; status: string; refunded: boolean }> {
  const res = await apiFetch(`${API_BASE}/v1/generations/${encodeURIComponent(jobId)}/cancel`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`cancel failed (${res.status})`);
  return res.json();
}

